import React from "react";
import { Box, Chip, InputBase, MenuItem, Select, Stack, Switch } from "@mui/material";
import { alpha } from "@mui/material/styles";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { useIntl } from "react-intl";
import RevaText from "components/common/RevaText";
import {
  MATCH_SCORING_PRESET_LABELS,
  MAX_POINTS,
  matchScoringPreview,
  normalizeMatchScoring,
  toggleSetMargin,
  updateMatchScoringField,
  updateSetMarginField,
} from "./matchScoring";

const BASE_FIELDS = [
  { field: "win", label: "matchScoring.field.win" },
  { field: "tie", label: "matchScoring.field.tie" },
  { field: "loss", label: "matchScoring.field.loss" },
  { field: "walkover_win", label: "matchScoring.field.walkoverWin" },
  { field: "walkover_loss", label: "matchScoring.field.walkoverLoss" },
];

const WIN_MARGIN_FIELDS = [
  { field: "win_wide", label: "matchScoring.field.winWide", example: "2-0 / 3-0", tone: "win" },
  { field: "win_close", label: "matchScoring.field.winClose", example: "2-1 / 3-2", tone: "win" },
];

const LOSS_MARGIN_FIELDS = [
  { field: "loss_close", label: "matchScoring.field.lossClose", example: "1-2 / 2-3", tone: "decidingLoss" },
  { field: "loss_wide", label: "matchScoring.field.lossWide", example: "0-2 / 0-3", tone: "neutral" },
];

// Theme palette path per tone; neutral rows use the regular text color.
const TONE_COLOR = {
  win: "primary.main",
  decidingLoss: "warning.main",
  neutral: "text.secondary",
  walkoverLoss: "error.main",
};

const borderColor = (theme) => theme.custom?.border?.default || theme.palette.divider;

export const SectionLabel = ({ id, color = "text.secondary", sx }) => (
  <RevaText
    id={id}
    variant="overline"
    color={color}
    sx={{ display: "block", fontWeight: 600, letterSpacing: "0.08em", lineHeight: 1.6, ...sx }}
  />
);

const Dot = ({ color, size = 8 }) => (
  <Box component="span" sx={{ width: size, height: size, borderRadius: "50%", bgcolor: color, flexShrink: 0 }} />
);

/** Row with a title + description on the left and a switch on the right. */
export const SwitchRow = ({ titleId, descriptionId, checked, onChange, disabled, showDot = false }) => (
  <Stack direction="row" spacing={2} alignItems="flex-start" justifyContent="space-between">
    <Box sx={{ opacity: disabled && !checked ? 0.6 : 1 }}>
      <Stack direction="row" spacing={1} alignItems="center">
        {showDot && checked && <Dot color="primary.main" />}
        <RevaText id={titleId} variant="subtitle1" sx={{ fontWeight: 600 }} />
      </Stack>
      <RevaText id={descriptionId} variant="body2" color="text.secondary" />
    </Box>
    <Switch checked={checked} disabled={disabled} onChange={(event) => onChange(event.target.checked)} />
  </Stack>
);

const PointCard = ({ label, example, value, onChange, readOnly, tone = "default" }) => {
  const intl = useIntl();

  return (
    <Box
      sx={(theme) => ({
        border: `1px solid ${borderColor(theme)}`,
        borderRadius: 2,
        bgcolor: "background.base",
        px: 2,
        py: 1.5,
        minWidth: 0,
        transition: theme.transitions.create("border-color"),
        "&:focus-within": { borderColor: "primary.main" },
      })}
    >
      <RevaText variant="body2" color="text.secondary" noWrap>
        {intl.formatMessage({ id: label })}
      </RevaText>
      {example && (
        <RevaText variant="caption" sx={{ display: "block", fontFamily: "monospace", color: "text.muted" }}>
          {intl.formatMessage({ id: "matchScoring.example" }, { example })}
        </RevaText>
      )}
      <Stack direction="row" alignItems="baseline" spacing={1} sx={{ mt: 1 }}>
        <InputBase
          type="number"
          value={value}
          readOnly={readOnly}
          onChange={(event) => onChange(event.target.value)}
          inputProps={{
            min: 0,
            max: MAX_POINTS,
            step: 1,
            "aria-label": intl.formatMessage({ id: label }),
          }}
          sx={{
            flex: 1,
            fontSize: "1.5rem",
            fontWeight: 700,
            color: tone === "default" ? "text.primary" : TONE_COLOR[tone],
            "& input": { p: 0 },
          }}
        />
        <RevaText variant="body2" color="text.secondary">
          {intl.formatMessage({ id: "matchScoring.pointsUnit" }, { points: Number(value) })}
        </RevaText>
      </Stack>
    </Box>
  );
};

const OutcomeGroup = ({ titleId, tone, fields, margin, readOnly, onChange }) => (
  <Box sx={(theme) => ({ border: `1px solid ${borderColor(theme)}`, borderRadius: 2, p: 2 })}>
    <Stack
      direction="row"
      spacing={1}
      alignItems="center"
      sx={(theme) => ({ pb: 1.5, mb: 2, borderBottom: `1px solid ${theme.palette.divider}` })}
    >
      <Dot color={TONE_COLOR[tone]} />
      <SectionLabel id={titleId} color={tone === "win" ? "primary.main" : "text.secondary"} />
    </Stack>
    <Box sx={{ display: "grid", gap: 2, gridTemplateColumns: "repeat(2, minmax(0, 1fr))" }}>
      {fields.map(({ field, label, example, tone: fieldTone }) => (
        <PointCard
          key={field}
          label={label}
          example={example}
          tone={fieldTone}
          value={margin[field]}
          readOnly={readOnly}
          onChange={(points) => onChange(field, points)}
        />
      ))}
    </Box>
  </Box>
);

const PointsPill = ({ row }) => {
  const intl = useIntl();
  const label =
    typeof row.points === "object"
      ? intl.formatMessage({ id: "matchScoring.pointsPair" }, row.points)
      : intl.formatMessage({ id: "matchScoring.points" }, { points: row.points });
  const highlighted = row.tone === "win" || row.tone === "decidingLoss";

  return (
    <Chip
      size="small"
      variant="outlined"
      label={label}
      sx={(theme) => {
        const color = row.tone === "decidingLoss" ? theme.palette.warning.main : theme.palette.primary.main;
        return highlighted
          ? { fontWeight: 700, color, borderColor: alpha(color, 0.5), bgcolor: alpha(color, 0.12) }
          : { fontWeight: 600, color: "text.primary", borderColor: borderColor(theme) };
      }}
    />
  );
};

const PreviewList = ({ scoring }) => {
  const intl = useIntl();

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2} sx={{ mb: 1 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <VisibilityOutlinedIcon fontSize="small" sx={{ color: "text.secondary" }} />
          <SectionLabel id="matchScoring.previewTitle" />
        </Stack>
        <RevaText
          id="matchScoring.previewHint"
          variant="caption"
          color="text.secondary"
          sx={{ display: { xs: "none", sm: "block" } }}
        />
      </Stack>
      <Box sx={(theme) => ({ border: `1px solid ${borderColor(theme)}`, borderRadius: 2, overflow: "hidden" })}>
        {matchScoringPreview(scoring).map((row, index) => (
          <Stack
            key={row.id}
            direction="row"
            alignItems="center"
            spacing={1.5}
            sx={(theme) => ({
              px: 2,
              py: 1.25,
              borderTop: index === 0 ? "none" : `1px solid ${theme.palette.divider}`,
            })}
          >
            <Dot color={TONE_COLOR[row.tone]} size={6} />
            <RevaText variant="body2" sx={{ flex: 1, minWidth: 0 }}>
              {intl.formatMessage({ id: row.id })}
            </RevaText>
            {row.badgeId && (
              <Chip
                size="small"
                variant="outlined"
                color="warning"
                label={intl.formatMessage({ id: row.badgeId })}
                sx={{ display: { xs: "none", sm: "inline-flex" } }}
              />
            )}
            <PointsPill row={row} />
          </Stack>
        ))}
      </Box>
    </Box>
  );
};

/**
 * Editor for a match scoring config ("Puntos por partido"), following the backend JSON shape.
 * With `readOnly`, it shows the rule that applies (e.g. the sport defaults) without letting it change:
 * `readOnlyLabel` replaces the template name and `readOnlyBadgeId` explains where the rule comes from.
 */
const MatchScoringEditor = ({
  value,
  onChange = () => {},
  presets = [],
  readOnly = false,
  readOnlyLabel = null,
  readOnlyBadgeId = null,
}) => {
  const intl = useIntl();
  const scoring = normalizeMatchScoring(value);
  const presetKeys = [...presets.map((preset) => preset.preset), "custom"];

  const handlePresetChange = (presetKey) => {
    const preset = presets.find((candidate) => candidate.preset === presetKey);
    onChange(preset ? normalizeMatchScoring(preset) : { ...scoring, preset: "custom" });
  };

  return (
    <Stack spacing={3}>
      <Box>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
          <SectionLabel id="matchScoring.preset" />
          {readOnly && readOnlyBadgeId && (
            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ color: "primary.main" }}>
              <LockOutlinedIcon sx={{ fontSize: 16 }} />
              <RevaText id={readOnlyBadgeId} variant="caption" color="inherit" />
            </Stack>
          )}
        </Stack>
        <Select
          fullWidth
          size="small"
          readOnly={readOnly}
          value={readOnly ? "inherited" : scoring.preset}
          onChange={(event) => handlePresetChange(event.target.value)}
          renderValue={(selected) => (
            <Stack direction="row" spacing={1} alignItems="center">
              {readOnly && <Dot color="primary.main" />}
              <span>
                {selected === "inherited"
                  ? readOnlyLabel
                  : intl.formatMessage({ id: MATCH_SCORING_PRESET_LABELS[selected] || selected })}
              </span>
            </Stack>
          )}
          sx={{ mt: 0.5, bgcolor: "background.base" }}
        >
          {readOnly ? (
            <MenuItem value="inherited">{readOnlyLabel}</MenuItem>
          ) : (
            presetKeys.map((key) => (
              <MenuItem key={key} value={key}>
                {intl.formatMessage({ id: MATCH_SCORING_PRESET_LABELS[key] || key })}
              </MenuItem>
            ))
          )}
        </Select>
      </Box>

      <Box>
        <SectionLabel id="matchScoring.pointsSection" sx={{ mb: 1 }} />
        <Box
          sx={{
            display: "grid",
            gap: 2,
            gridTemplateColumns: {
              xs: "repeat(2, minmax(0, 1fr))",
              sm: "repeat(3, minmax(0, 1fr))",
              md: "repeat(5, minmax(0, 1fr))",
            },
          }}
        >
          {BASE_FIELDS.map(({ field, label }) => (
            <PointCard
              key={field}
              label={label}
              value={scoring[field]}
              readOnly={readOnly}
              onChange={(points) => onChange(updateMatchScoringField(scoring, field, points, presets))}
            />
          ))}
        </Box>
      </Box>

      <Box sx={(theme) => ({ borderTop: `1px solid ${theme.palette.divider}`, pt: 3 })}>
        <SwitchRow
          titleId="matchScoring.bySets"
          descriptionId="matchScoring.bySetsHelp"
          checked={!!scoring.by_set_margin}
          disabled={readOnly}
          showDot
          onChange={(enabled) => onChange(toggleSetMargin(scoring, enabled, presets))}
        />
      </Box>

      {scoring.by_set_margin && (
        <Box sx={{ display: "grid", gap: 2, gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" } }}>
          <OutcomeGroup
            titleId="matchScoring.winOutcomes"
            tone="win"
            fields={WIN_MARGIN_FIELDS}
            margin={scoring.by_set_margin}
            readOnly={readOnly}
            onChange={(field, points) => onChange(updateSetMarginField(scoring, field, points, presets))}
          />
          <OutcomeGroup
            titleId="matchScoring.lossOutcomes"
            tone="neutral"
            fields={LOSS_MARGIN_FIELDS}
            margin={scoring.by_set_margin}
            readOnly={readOnly}
            onChange={(field, points) => onChange(updateSetMarginField(scoring, field, points, presets))}
          />
        </Box>
      )}

      <Box sx={(theme) => ({ borderTop: `1px solid ${theme.palette.divider}`, pt: 3 })}>
        <PreviewList scoring={scoring} />
      </Box>
    </Stack>
  );
};

export default MatchScoringEditor;
